import { View, Pressable } from 'react-native'
import { useTranslation } from 'react-i18next'
import { interpolate, useAnimatedStyle } from 'react-native-reanimated'
import Typography from '@ui/typography'
import Checkbox from '@ui/forms/Checkbox'
import { colors } from '@ui/colors'
import cn from '@ui/utils/cn'
import { hex } from '@ui/utils/hex'
import { colorActive } from '@screens/checkout/OrderCheckbox/styles'
import Border from '@ui/effects/Border'
import Gradient from '@ui/effects/Gradient'
import useAnimatedPress from '@ui/animated/useAnimatedPress'
import { LinkedOrdersOption } from './types'

interface Props {
  option: LinkedOrdersOption
  onSelect?: (option: LinkedOrdersOption) => void
}

export default function OptionItem({ option, onSelect }: Props) {
  const { t } = useTranslation(['modals', 'ui'])
  const text = `modals:checkout.linkedOrders.${option}`

  const { animation, onPressIn, onPressOut } = useAnimatedPress()

  const stylePressable = useAnimatedStyle(() => ({
    opacity: interpolate(animation.value, [0, 1], [0, 1]),
  }))

  return (
    <Pressable
      className="relative inset-0 w-full overflow-hidden"
      style={{ borderRadius }}
      onPressIn={onPressIn}
      onPressOut={onPressOut}
      onPress={() => onSelect?.(option)}
    >
      <Gradient
        colors={[colors.gray[75], colors.gray[50], colors.white]}
        start={[0, 0.5]}
        end={[1, 0.5]}
      />
      <Gradient
        colors={[colors.gray[200], colors.gray[100], colors.white]}
        start={[0, 0.5]}
        end={[1, 0.5]}
        style={stylePressable}
      />
      <Border style={{ borderRadius }} />
      <View className="flex w-full flex-row items-start justify-between">
        <View className="flex w-2/3 flex-col p-8 pr-0">
          <View className="flex flex-col justify-between gap-4">
            <Typography variant="LGB">{t(`${text}.title`)}</Typography>
            <View className="flex flex-col gap-1">
              <Typography variant="MD" className="color-desc">
                {t(`${text}.desc`)}
                {option === 'together' && (
                  <Typography variant="MD" className="color-red-700 opacity-75">
                    {' '}
                    {t(`${text}.warning`)}
                  </Typography>
                )}
              </Typography>
            </View>
          </View>
        </View>
        <View
          className="flex h-full w-full flex-1"
          style={{ transform: [{ scale: 0.75 }], transformOrigin: 'right' }}
        >
          <OrdersMock option={option} />
        </View>
      </View>
    </Pressable>
  )
}

const OrdersMock = ({ option }: { option: LinkedOrdersOption }) => {
  const { t } = useTranslation(['modals', 'ui'])
  const text = `modals:checkout.linkedOrders`
  const isActive = option === 'together'
  const borderColor = isActive ? colorActive : colors.black10

  return (
    <View
      className="flex flex-col items-start justify-start gap-0 overflow-hidden rounded-xl rounded-r-none border-2 border-r-0"
      style={[
        {
          borderColor,
        },
        !isActive && { borderWidth: 0 },
      ]}
    >
      <OrderMock option={option} label={t(`${text}.primary`)} checked={true} />
      {isActive && (
        <View
          style={{ backgroundColor: borderColor, width: '100%', height: 2 }}
        />
      )}
      <OrderMock
        option={option}
        label={t(`${text}.dependent`)}
        checked={isActive}
      />
    </View>
  )
}

const OrderMock = ({
  label,
  checked,
  option,
}: {
  label: string
  checked: boolean
  option: LinkedOrdersOption
}) => {
  return (
    <View
      className={cn('flex w-full')}
      style={{
        backgroundColor: checked ? hex(colorActive, 0.1) : colors.gray[75],
      }}
    >
      <View className="flex w-full flex-1 flex-row items-center gap-8 p-8">
        <Checkbox value={checked} icon="plus" colorActive={colors.blue[500]} />
        <Typography variant="MDB">{label}</Typography>
      </View>
      {checked && option === 'separately' && (
        <Border
          className="absolute bottom-0 left-0 right-0 top-0 rounded-xl rounded-r-none border-2 border-r-0"
          style={{
            borderColor: colorActive,
          }}
        />
      )}
    </View>
  )
}

const borderRadius = 24
